import { useState } from 'react';

import type {
  BankReconAdjustmentType,
  Branch,
  CreateBankReconAdjustment,
  Department,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { type AdjustmentDir, signedAdjustmentAmount } from './recon-helpers';
import { useBranches } from '../../lib/queries/branches';
import { useActiveCompanyId } from '../../lib/queries/companies';
import { useDepartments } from '../../lib/queries/departments';

const TYPE_LABELS: Readonly<Record<BankReconAdjustmentType, string>> = {
  BANK_FEE: 'ค่าธรรมเนียมธนาคาร (ลง JV)',
  INTEREST: 'ดอกเบี้ยรับ (ลง JV)',
  FX: 'ผลต่างอัตราแลกเปลี่ยน (ลง JV)',
  CHECK_NOT_CLEARED: 'เช็คยังไม่ขึ้นเงิน (ไม่ลง JV)',
  DEPOSIT_IN_TRANSIT: 'เงินฝากระหว่างทาง (ไม่ลง JV)',
  OTHER: 'อื่นๆ (ไม่ลง JV)',
};

interface Props {
  statementId: string;
  onSubmit: (data: CreateBankReconAdjustment) => void;
  onClose: () => void;
  isPending: boolean;
}

export function AddAdjustmentModal({
  statementId: _statementId,
  onSubmit,
  onClose,
  isPending,
}: Props) {
  const [type, setType] = useState<BankReconAdjustmentType>('BANK_FEE');
  const [magnitude, setMagnitude] = useState('');
  const [description, setDescription] = useState('');
  const [dir, setDir] = useState<AdjustmentDir>('gain');
  const [branchId, setBranchId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  // Stable idempotency token for this modal instance — reused on retry / double-click
  // so the server replay-gates the duplicate POST; fresh on reopen (the modal
  // unmounts on close). Lazy initializer avoids the react-hooks/purity lint that
  // fires when crypto.randomUUID() is called at the top level of render.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const companyId = useActiveCompanyId();
  const branches: Branch[] = useBranches(companyId).data ?? [];
  const departments: Department[] = useDepartments(companyId).data ?? [];

  const usesDir = type === 'FX' || type === 'OTHER';
  const mag = Number(magnitude) || 0;
  const canSubmit = mag > 0 && description.trim().length > 0;

  function submit() {
    if (!canSubmit) return;
    const data: CreateBankReconAdjustment = {
      type,
      amount: signedAdjustmentAmount(type, mag, dir),
      description: description.trim(),
    };
    if (branchId) data.branchId = branchId;
    if (departmentId) data.departmentId = departmentId;
    data.idempotencyKey = idempotencyKey;
    onSubmit(data);
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 620,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>เพิ่มรายการปรับปรุง</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label>
            ประเภท
            <select
              aria-label="ประเภท"
              className="select"
              value={type}
              onChange={(e) => setType(e.target.value as BankReconAdjustmentType)}
              style={{ width: '100%', height: 38 }}
            >
              {Object.entries(TYPE_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          {usesDir && (
            <label>
              ทิศทาง
              <select
                aria-label="ทิศทาง"
                className="select"
                value={dir}
                onChange={(e) => setDir(e.target.value as AdjustmentDir)}
                style={{ width: '100%', height: 38 }}
              >
                <option value="gain">เพิ่มยอดเงินฝาก (+)</option>
                <option value="loss">ลดยอดเงินฝาก (−)</option>
              </select>
            </label>
          )}
          <label>
            จำนวน
            <input
              aria-label="จำนวน"
              className="input"
              type="number"
              min="0"
              value={magnitude}
              onChange={(e) => setMagnitude(e.target.value)}
              placeholder="0.00"
              style={{ width: '100%', height: 38 }}
            />
          </label>
          <label>
            คำอธิบาย
            <input
              aria-label="คำอธิบาย"
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="เช่น ค่าธรรมเนียมโอนเงิน"
              style={{ width: '100%', height: 38 }}
            />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>
              สาขา (ถ้าจำเป็น)
              <select
                aria-label="สาขา"
                className="select"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                style={{ width: '100%', height: 38 }}
              >
                <option value="">— ไม่ระบุ —</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.code} {b.nameTh}
                  </option>
                ))}
              </select>
            </label>
            <label>
              แผนก (ถ้าจำเป็น)
              <select
                aria-label="แผนก"
                className="select"
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                style={{ width: '100%', height: 38 }}
              >
                <option value="">— ไม่ระบุ —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.code} {d.nameTh}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button className="btn primary" disabled={!canSubmit || isPending} onClick={submit}>
            <Icon name="check" size={14} /> บันทึกปรับปรุง
          </button>
        </div>
      </div>
    </div>
  );
}
